import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { getTeamLogoUrl } from '../utils/logoUrl';
import { formatDate } from '../utils/dateUtils';
import { invalidateUpcomingMatchesCache } from '../utils/cacheUtils';
import ScoreInput from '../components/ScoreInput';
import BankerButton from '../components/BankerButton';
import { getBankerMatchIdInWeek } from '../utils/bankerUtils';

interface Team {
  id: string;
  name: string;
  code: string;
  logoUrl?: string;
}

interface Match {
 id: string;
  season: { id: string; name: string };
  matchGroup: { id: string; name: string; matchWeek?: number };
  homeTeam: Team;
  awayTeam: Team;
  matchDate: string;
  venue: string;
  isCup: boolean;
  predictionsClose: string;
  status: string;
  homeGoals?: number;
  awayGoals?: number;
}

interface Prediction {
  id?: string;
  match: string;
  homeGoals: string | number;
  awayGoals: string | number;
  isBanker: boolean;
}

interface WeekPrediction {
  match: { id: string };
  isBanker: boolean;
}

interface TeamForm {
  team: {
    id: string;
    name: string;
  };
  form: Array<{
    matchId: string;
    matchDate: string;
    venue: string | null;
    isHome: boolean;
    opponent: {
      id: string;
      name: string;
      shortName: string | null;
      logoUrl: string | null;
    };
    teamGoals: number;
    opponentGoals: number;
    result: 'win' | 'loss' | 'draw';
  }>;
  summary: {
    wins: number;
    losses: number;
    draws: number;
    total: number;
  };
}

const MatchMetaHeader: React.FC<{ match: Match }> = ({ match }) => (
  <div className="flex items-center justify-between gap-2 mb-4">
    <div className="flex items-center gap-2 min-w-0">
      <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
        {formatDate(match.matchDate)}
      </div>
      {match.isCup && (
        <span
          className="px-2 py-1 text-xs rounded flex-shrink-0"
          style={{ backgroundColor: 'rgba(234, 179, 8, 0.2)', color: '#eab308' }}
        >
          Cup
        </span>
      )}
    </div>
    <div className="text-sm font-semibold truncate text-right" style={{ color: 'var(--text-secondary)' }}>
      {match.season.name}
    </div>
  </div>
);

const Predictions: React.FC = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [match, setMatch] = useState<Match | null>(null);
  const [prediction, setPrediction] = useState<Prediction>({
    match: matchId || '',
    homeGoals: '',
    awayGoals: '',
    isBanker: false
  });
  const [homeTeamForm, setHomeTeamForm] = useState<TeamForm | null>(null);
  const [awayTeamForm, setAwayTeamForm] = useState<TeamForm | null>(null);
  const [weekPredictions, setWeekPredictions] = useState<WeekPrediction[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Helper function to get matchweek parameter
  const getMatchweekParam = (matchData: Match | null): string => {
    if (!matchData) return 'current';
    
    let matchWeek = matchData.matchGroup?.matchWeek;
    if (!matchWeek && matchData.matchGroup?.name) {
      // Try to extract matchweek from name if it contains a number
      const nameMatch = matchData.matchGroup.name.match(/matchweek\s*(\d+)/i);
      if (nameMatch) {
        matchWeek = parseInt(nameMatch[1]);
      }
    }
    
    return matchWeek ? `matchweek-${matchWeek}` : 'current';
  };

  useEffect(() => {
    const fetchMatchData = async () => {
      try {
        setLoading(true);
        
        // Fetch match details
        const matchResponse = await apiClient.get(`/matches/${matchId}`);
        console.log('API response for match:', matchResponse.data);
        const matchData = matchResponse.data;
        setMatch(matchData);
        
        // Fetch team forms in parallel
        try {
          const [homeFormResponse, awayFormResponse] = await Promise.all([
            apiClient.get(`/teams/${matchData.homeTeam.id}/form?limit=5`).catch(() => null),
            apiClient.get(`/teams/${matchData.awayTeam.id}/form?limit=5`).catch(() => null)
          ]);
          
          if (homeFormResponse?.data) {
            setHomeTeamForm(homeFormResponse.data);
          }
          if (awayFormResponse?.data) {
            setAwayTeamForm(awayFormResponse.data);
          }
        } catch (err) {
          // Team form fetch failed, that's okay - we'll just not show form
          console.warn('Failed to fetch team forms:', err);
        }
        
        // Fetch existing prediction if any
        try {
          const predictionResponse = await apiClient.get(`/predictions?match=${matchId}`);
        if (predictionResponse.data.length > 0) {
          const pred = predictionResponse.data[0];
          setPrediction({
            ...pred,
            homeGoals: pred.homeGoals?.toString() ?? '',
            awayGoals: pred.awayGoals?.toString() ?? '',
          });
        }
        } catch (err) {
          // No existing prediction, that's fine
        }

        if (matchData.matchGroup?.id) {
          try {
            const weekResponse = await apiClient.get(`/predictions?matchGroup=${matchData.matchGroup.id}`);
            setWeekPredictions(weekResponse.data);
          } catch (err) {
            // Week predictions unavailable, banker visibility falls back to this match only
          }
        }
        
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load match data');
      } finally {
        setLoading(false);
      }
    };

    if (matchId) {
      fetchMatchData();
    }
  }, [matchId]);

  const matchIdsInWeek = useMemo(() => {
    const ids = new Set<string>();
    if (matchId) {
      ids.add(matchId);
    }
    weekPredictions.forEach(weekPrediction => ids.add(weekPrediction.match.id));
    return Array.from(ids);
  }, [matchId, weekPredictions]);

  const bankerMatchIdInWeek = useMemo(() => {
    if (!matchId) {
      return null;
    }
    return getBankerMatchIdInWeek(matchIdsInWeek, weekPredictions, {
      [matchId]: { isBanker: prediction.isBanker },
    });
  }, [matchId, matchIdsInWeek, weekPredictions, prediction.isBanker]);

  const handleBankerToggle = () => {
    if (!matchId) {
      return;
    }
    setPrediction(prev => ({
      ...prev,
      isBanker: !prev.isBanker,
    }));
  };

  const isPredictionOpen = (): boolean => {
    if (!match) return false;
    return new Date() < new Date(match.predictionsClose);
  };

  // Component to display team form indicators
  const TeamFormIndicator: React.FC<{ form: TeamForm | null }> = ({ form }) => {
    if (!form || form.form.length === 0) {
      return null;
    }

    return (
      <div className="flex justify-center gap-1 sm:gap-1.5 mt-2">
        {form.form.slice().reverse().map((result, index) => {
          let bgColor: string;
          let textColor: string;
          
          if (result.result === 'win') {
            bgColor = '#10b981'; // green
            textColor = '#ffffff';
          } else if (result.result === 'loss') {
            bgColor = '#ef4444'; // red
            textColor = '#ffffff';
          } else {
            bgColor = '#6b7280'; // grey
            textColor = '#ffffff';
          }

          return (
            <div
              key={`${result.matchId}-${index}`}
              className="flex items-center justify-center font-bold text-[10px] sm:text-xs w-[18px] h-[18px] sm:w-6 sm:h-6"
              style={{
                backgroundColor: bgColor,
                color: textColor,
                borderRadius: '4px', // Rounded corners for squircle effect
              }}
            >
              {result.result === 'win' ? 'W' : result.result === 'loss' ? 'L' : 'D'}
            </div>
          );
        })}
      </div>
    );
  };



const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  if (!isPredictionOpen()) {
    setError('Predictions are closed for this match');
    return;
  }

  if (
    prediction.homeGoals === '' ||
    prediction.awayGoals === '' ||
    Number(prediction.homeGoals) < 0 ||
    Number(prediction.awayGoals) < 0
  ) {
    setError('Scores cannot be empty or negative');
    return;
  }

  try {
    setSaving(true);
    setError('');

    const payload = {
      ...prediction,
      homeGoals: Number(prediction.homeGoals),
      awayGoals: Number(prediction.awayGoals),
    };

    if (prediction.id) {
      // Update existing prediction
      await apiClient.put(`/predictions/${prediction.id}`, payload);
    } else {
      // Create new prediction
      await apiClient.post('/predictions', payload);
    }

    // Navigate back to season detail with the specific matchweek
    console.log('Match data for navigation:', {
      matchGroup: match?.matchGroup,
      matchWeek: match?.matchGroup?.matchWeek,
      fullMatch: match
    });
    
    const matchweekParam = getMatchweekParam(match);
    console.log('Navigating to:', `/seasons/${match?.season.id}?matchweek=${matchweekParam}`);
    
    navigate(`/seasons/${match?.season.id}?matchweek=${matchweekParam}`);
    invalidateUpcomingMatchesCache();

  } catch (err: any) {
    setError(err.response?.data?.message || 'Failed to save prediction');
  } finally {
    setSaving(false);
  }
};

  if (loading) {
    return (
      <div className="flex items-center justify-center">
        <div className="text-xl">Loading match data...</div>
      </div>
    );
  }

  if (error && !match) {
    return (
      <div className="flex items-center justify-center">
        <div className="text-red-600 text-xl">{error}</div>
      </div>
    );
  }

  if (!match) {
    return (
      <div className="flex items-center justify-center">
        <div className="text-xl">Match not found</div>
      </div>
    );
  }

  return (
    <div className="" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="max-w-2xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to={`/seasons/${match.season.id}?matchweek=${getMatchweekParam(match)}`}
            className="mb-4 inline-block"
            style={{ color: 'var(--highlight)' }}
          >
            ← Back to Competition
          </Link>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>Make Prediction</h1>
        </div>


        {/* Prediction Form */}
        {isPredictionOpen() ? (
          <form onSubmit={handleSubmit} className="card rounded-lg shadow p-6 bg-gray-50 dark:bg-gray-800/50">
            {error && (
              <div className="border px-4 py-3 rounded mb-4" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', borderColor: '#ef4444', color: '#ef4444' }}>
                {error}
              </div>
            )}

            <MatchMetaHeader match={match} />

            <div className="text-center pt-4 mb-4">
              <div className="grid grid-cols-3 gap-4 items-center">
                <div className="text-center">
                  {match.homeTeam.logoUrl && (
                    <img
                      src={getTeamLogoUrl(match.homeTeam.logoUrl)}
                      alt={`${match.homeTeam.name} logo`}
                      className="w-10 h-10 object-contain justify-center mx-auto mb-2"
                    />
                  )}
                  <div className="font-semibold text-lg" style={{ color: 'var(--text-primary)' }}>{match.homeTeam.name}</div>
                  <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>{match.homeTeam.code}</div>
                  <TeamFormIndicator form={homeTeamForm} />
                </div>

                <div className="text-center">
                  <div className="text-2xl font-bold" style={{ color: 'var(--text-secondary)' }}>vs</div>
                </div>

                <div className="text-center">
                  {match.awayTeam.logoUrl && (
                    <img
                      src={getTeamLogoUrl(match.awayTeam.logoUrl)}
                      alt={`${match.awayTeam.name} logo`}
                      className="w-10 h-10 object-contain justify-center mx-auto mb-2"
                    />
                  )}
                  <div className="font-semibold text-lg" style={{ color: 'var(--text-primary)' }}>{match.awayTeam.name}</div>
                  <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>{match.awayTeam.code}</div>
                  <TeamFormIndicator form={awayTeamForm} />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 items-center mb-6">
              <div className="text-center">
                <ScoreInput
                  value={prediction.homeGoals.toString()}
                  onChange={(value) => setPrediction({
                    ...prediction,
                    homeGoals: value
                  })}
                  min={0}
                  max={9}
                />
              </div>
              
              <div className="text-center text-2xl font-bold" style={{ color: 'var(--text-secondary)' }}>
                -
              </div>
              
              <div className="text-center">
                <ScoreInput
                  value={prediction.awayGoals.toString()}
                  onChange={(value) => setPrediction({
                    ...prediction,
                    awayGoals: value
                  })}
                  min={0}
                  max={9}
                />
              </div>
            </div>

            <div className="mb-6 flex justify-center">
              {(!bankerMatchIdInWeek || bankerMatchIdInWeek === matchId) && (
                <BankerButton
                  isActive={prediction.isBanker}
                  onToggle={handleBankerToggle}
                  tooltipText={
                    <>
                      Banker predictions earn double points if correct, but lose 1 point if incorrect.
                      <br />
                      They can be used once per round.
                    </>
                  }
                />
              )}
            </div>

            <div className="flex justify-center space-x-4">
              {prediction.id && (
                <Link
                  to={`/seasons/${match.season.id}?matchweek=${getMatchweekParam(match)}`}
                  className="btn-secondary px-4 py-2 rounded-md text-sm font-medium"
                >
                  Reset
                </Link>
              )}
              <button
                type="submit"
                disabled={saving || prediction.homeGoals === '' || prediction.awayGoals === ''}
                className="btn-primary px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : (prediction.id ? 'Update Prediction' : 'Submit Prediction')}
              </button>
            </div>
          </form>
        ) : (
          <div className="card rounded-lg shadow p-6 bg-gray-50 dark:bg-gray-800/50">
            <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Match Result</h2>
            <MatchMetaHeader match={match} />

            <div className="text-center pt-4 mb-6">
              <div className="grid grid-cols-3 gap-4 items-center">
              <div className="text-center">
                {match.homeTeam.logoUrl && (
                   <img 
                     src={getTeamLogoUrl(match.homeTeam.logoUrl)}
                     alt={`${match.homeTeam.name} logo`}
                     className="w-10 h-10 object-contain justify-center mx-auto mb-2"
                   />
                 )}
                <div className="font-semibold text-lg" style={{ color: 'var(--text-primary)' }}>{match.homeTeam.name}</div>
                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>{match.homeTeam.code}</div>
                <TeamFormIndicator form={homeTeamForm} />
              </div>
              
                             <div className="text-center">
                 {match.homeGoals !== undefined && match.homeGoals !== null && match.awayGoals !== undefined && match.awayGoals !== null ? (
                   <div>
                     <div className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
                       {match.homeGoals} - {match.awayGoals}
                     </div>
                     <div className="text-sm font-semibold" style={{ color: '#22c55e' }}>Final Result</div>
                   </div>
                 ) : (
                   <div>
                     <div className="text-2xl font-bold" style={{ color: 'var(--text-secondary)' }}>vs</div>
                     <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Result Pending</div>
                   </div>
                 )}
               </div>
              
              <div className="text-center">
                {match.awayTeam.logoUrl && (
                   <img 
                     src={getTeamLogoUrl(match.awayTeam.logoUrl)}
                     alt={`${match.awayTeam.name} logo`}
                     className="w-10 h-10 object-contain justify-center mx-auto mb-2"
                   />
                 )}
                <div className="font-semibold text-lg" style={{ color: 'var(--text-primary)' }}>{match.awayTeam.name}</div>
                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>{match.awayTeam.code}</div>
                <TeamFormIndicator form={awayTeamForm} />
              </div>
              </div>
            </div>

            {/* Show user's prediction if they made one */}
            {prediction.homeGoals !== '' && prediction.awayGoals !== '' && (
              <div className={`mb-6 p-4 rounded-lg ${
                match.homeGoals !== undefined && match.homeGoals !== null && 
                match.awayGoals !== undefined && match.awayGoals !== null
                  ? (() => {
                      // Determine if prediction is correct, exact score, or incorrect
                      const isExactScore = match.homeGoals === prediction.homeGoals && 
                                          match.awayGoals === prediction.awayGoals;
                      const isCorrectResult = !isExactScore && (
                        (match.homeGoals > match.awayGoals && prediction.homeGoals > prediction.awayGoals) ||
                        (match.homeGoals < match.awayGoals && prediction.homeGoals < prediction.awayGoals) ||
                        (match.homeGoals === match.awayGoals && prediction.homeGoals === prediction.awayGoals)
                      );
                      
                      if (isExactScore) {
                        return 'bg-yellow-200 dark:bg-yellow-200/15 border border-yellow-400 dark:border-yellow-200';
                      } else if (isCorrectResult) {
                        return 'bg-green-200 dark:bg-green-600/20 border border-green-400 dark:border-green-800';
                      } else {
                        return 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800';
                      }
                    })()
                  : 'bg-blue-100 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
              }`}>
                <div className="text-center">
                  <h3 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Your Prediction</h3>
                  <div className={`text-2xl font-bold ${
                    match.homeGoals !== undefined && match.homeGoals !== null && 
                    match.awayGoals !== undefined && match.awayGoals !== null
                      ? (() => {
                          const isExactScore = match.homeGoals === prediction.homeGoals && 
                                              match.awayGoals === prediction.awayGoals;
                          const isCorrectResult = !isExactScore && (
                            (match.homeGoals > match.awayGoals && prediction.homeGoals > prediction.awayGoals) ||
                            (match.homeGoals < match.awayGoals && prediction.homeGoals < prediction.awayGoals) ||
                            (match.homeGoals === match.awayGoals && prediction.homeGoals === prediction.awayGoals)
                          );
                          
                          if (isExactScore) {
                            return 'text-yellow-800 dark:text-yellow-200';
                          } else if (isCorrectResult) {
                            return 'text-green-800 dark:text-green-200';
                          } else {
                            return 'text-red-800 dark:text-red-200';
                          }
                        })()
                      : 'text-blue-800 dark:text-blue-200'
                  }`}>
                    {prediction.homeGoals} - {prediction.awayGoals}
                  </div>
                  {prediction.isBanker && (
                    <div className="text-sm mt-1" style={{ color: '#eab308' }}>★ Banker</div>
                  )}
                  
                  {/* Show prediction status if match has finished */}
                  {match.homeGoals !== undefined && match.homeGoals !== null && 
                   match.awayGoals !== undefined && match.awayGoals !== null && (
                    <div className="mt-2">
                      {(() => {
                        const isExactScore = match.homeGoals === prediction.homeGoals && 
                                            match.awayGoals === prediction.awayGoals;
                        const isCorrectResult = !isExactScore && (
                          (match.homeGoals > match.awayGoals && prediction.homeGoals > prediction.awayGoals) ||
                          (match.homeGoals < match.awayGoals && prediction.homeGoals < prediction.awayGoals) ||
                          (match.homeGoals === match.awayGoals && prediction.homeGoals === prediction.awayGoals)
                        );
                        
                        if (isExactScore) {
                          return (
                            <span className="text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 px-2 py-1 rounded font-medium">
                              Exact Score! 🎯
                            </span>
                          );
                        } else if (isCorrectResult) {
                          return (
                            <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded font-medium">
                              Correct Result! ✅
                            </span>
                          );
                        } else {
                          return (
                            <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2 py-1 rounded font-medium">
                              Incorrect ❌
                            </span>
                          );
                        }
                      })()}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="text-center">
              <div className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                Predictions are closed for this match
              </div>
              <Link
                to={`/seasons/${match.season.id}?matchweek=${getMatchweekParam(match)}`}
                className="btn-primary inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md"
              >
                Back to Competition
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Predictions; 